import { useAppContext } from '@/app/components/providers/AppContextProvider';
import { useOktaAuth } from '@okta/okta-react';
import { Icon, IconNames } from '@triumph/design-system';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DropdownItem,
  DropdownMenu,
  UserButton,
  UserProfileContainer,
  Username,
} from './UserProfile.StyledComponents';
import { UserInfo, UserProfileProps } from './UserProfile.types';
import { useNavigate } from 'react-router-dom';
import { PageRoutes } from '@/app/components/router/routes';
import { useFeatureFlags } from '@/app/shared/utils/hooks/useFeatureFlags';

export const UserProfile: React.FC<UserProfileProps> = ({ className }) => {
  const { t } = useTranslation('common');
  const { oktaAuth, authState } = useOktaAuth();
  const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false);
  const [userInfo, setUserInfo] = useState<UserInfo>({});
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const { theme } = useAppContext();
  const navigate = useNavigate();
  const { EnableNoaCenter, EnableCommunicationPreferences } = useFeatureFlags();

  // Extract user information from Okta auth state
  useEffect(() => {
    if (authState?.isAuthenticated && authState?.idToken?.claims) {
      const claims = authState.idToken.claims;
      setUserInfo({
        name: claims.name || claims.preferred_username || claims.email,
        email: claims.email,
        preferred_username: claims.preferred_username,
      });
    }
  }, [authState]);

  // Toggle dropdown
  const toggleDropdown = useCallback(() => {
    setIsDropdownOpen((prev) => !prev);
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        buttonRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false);
      }
    };

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen]);

  // Handle ESC key to close dropdown
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isDropdownOpen) {
        setIsDropdownOpen(false);
        buttonRef.current?.focus();
      }
    };

    if (isDropdownOpen) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isDropdownOpen]);

  // Handle sign out
  const handleSignOut = useCallback(async () => {
    try {
      setIsDropdownOpen(false);
      sessionStorage.removeItem('authToken');
      await oktaAuth.signOut();
    } catch (error) {
      console.error('Sign out error:', error);
    }
  }, [oktaAuth]);

  // Get display name
  const getDisplayName = (): string => {
    if (userInfo.preferred_username) {
      return userInfo.preferred_username;
    }
    if (userInfo.email) {
      return userInfo.email;
    }
    if (userInfo.name) {
      return userInfo.name;
    }

    return 'User';
  };

  // Don't render if not authenticated
  if (!authState?.isAuthenticated) {
    return null;
  }

  return (
    <UserProfileContainer className={className}>
      <UserButton
        ref={buttonRef}
        onClick={toggleDropdown}
        aria-expanded={isDropdownOpen}
        aria-haspopup="menu"
      >
        <Icon
          color={theme.colors.greyscale.white}
          name={IconNames.User_AccountAvatar}
          id={'user-profile-avatar-icon'}
          size={'md'}
        />
        <Username data-testid="profileName">{getDisplayName()}</Username>
        <Icon
          color={theme.colors.greyscale.white}
          name={
            isDropdownOpen ? IconNames.Arrow_CaretUp : IconNames.Arrow_CaretDown
          }
          id={'user-profile-arrow-icon'}
          size={'sm'}
        />
      </UserButton>

      <DropdownMenu ref={dropdownRef} isOpen={isDropdownOpen} role="menu">
        {EnableNoaCenter && (
          <DropdownItem
            onClick={() => navigate(PageRoutes.NOA_CENTER)}
            role="menuitem"
            data-testid="noaCenter"
          >
            {t('noaCenter', 'NOA Center')}
          </DropdownItem>
        )}
        {EnableCommunicationPreferences && (
          <DropdownItem
            onClick={() => navigate(PageRoutes.COMMUNICATION_PREFERENCES)}
            role="menuitem"
            data-testid="communicationPreferences"
          >
            {t('communicationPreferences', 'Communication Preferences')}
          </DropdownItem>
        )}
        <DropdownItem
          onClick={handleSignOut}
          role="menuitem"
          data-testid="signOut"
        >
          {t('signOut', 'Sign Out')}
        </DropdownItem>
      </DropdownMenu>
    </UserProfileContainer>
  );
};

export default UserProfile;
